import React, { useState, useCallback, useContext } from 'react';
import { connect, DispatchProp, useSelector } from 'react-redux';
import { CCardBody, CDataTable } from '@coreui/react';
import * as pluralize from 'pluralize';
import { useDropzone } from 'react-dropzone';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { UploadJobsRequest } from '../actions';
import { JobsUploadError, JobsUploadLineError } from '../../shared/models/jobs';
import { RootState } from '../../store/state';
import LoadingButton from '../../shared/components/LoadingButton';
import { selectOrgHierarchyRootOrg } from '../../store/selectors/orgHierarchy.selectors';
import { ConfigContext } from '../../shared/contexts/journeyConfig.context';

export interface BulkUploaderProps extends DispatchProp {
  uploadID?: string;
  uploadErrors: JobsUploadError;
}

const BulkUploader = (props: BulkUploaderProps) => {
  const { uploadID, uploadErrors, dispatch } = props;
  const [file, setFile] = useState<File>();
  const {
    enableOrgHierarchyModule,
  } = useFlags();
  const labels = useContext(ConfigContext);
  const rootOrg = useSelector(selectOrgHierarchyRootOrg);
    
    
  const onDrop = useCallback(acceptedFiles => {
    setFile(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: '.csv',
    maxFiles: 1,
    onDrop,
  });

  const handleOnClick = (input: File) => async () => {
    if (input !== undefined) {
      await dispatch(new UploadJobsRequest(input, uploadID));
      setFile(undefined);
   }
  };

  let dropzoneMessage = isDragActive ? 'Drop the file here' : 'Drag and drop a file here, or click to select a file';
  if (file !== undefined) {
    dropzoneMessage = `Current File: ${ file.name }`;
  }

  const transformedErrors = uploadErrors?.data.map(data => {
    const errors = data.errors || {};

    if (data.error) {
      const [,error] = data.error.split(':', 2);

      errors.invalid = error;
    }

    return {
      line: data.line,
      errors,
    };
  });

  return (
    <CCardBody>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <div { ...getRootProps({ className: 'dropzone' }) }>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <input { ...getInputProps() as any } />
        <p className='mb-0'>{ dropzoneMessage }</p>
      </div>
      <LoadingButton
        block
        isLoading={ false }
        color='primary'
        className="mt-3 bg-color-orange border-0 roundedBtn"
        onClick={ handleOnClick(file) }
        disabled={ file === undefined }
      >Upload Files</LoadingButton>

      <div className="mt-2">
        You can download an <a rel='noreferrer' target='_blank' href='https://glympsecdn.com/pro/example-job-upload-v1.2.csv'>example CSV</a> file that contains the required columns and formats.
      </div>
      {
        enableOrgHierarchyModule ? (
        <div className="mt-2 text-danger">
          All {pluralize(labels.task)} will be created under {rootOrg?.name} organization.
         </div>
        ) : null 
      }

      { transformedErrors != null && (
        <CDataTable
          items={ transformedErrors }
          hover
          striped
          fields={[
            'line',
            'errors',
          ]}
          scopedSlots={{
            'errors': ({ errors }: JobsUploadLineError) => {
              if (errors === undefined) {
                return <td>huh</td>
              }

              return (
                <td>{ Object.entries(errors).map(([key, value]) => `${ key.toLowerCase() }: ${ value }`).join(', ') }</td>
              );
            },
          }}
        />
      ) }
    </CCardBody>
  );
};

const mapStateToProps = (state: RootState) => ({
  uploadErrors: state?.jobs?.uploads?.uploadErrors,
});

export default connect(mapStateToProps)(BulkUploader);
